import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { TalentCard } from "@/components/TalentCard";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "");

export const DirectoryPage = ({ division, eyebrow, titleLines, intro, categories, pageTestid, note }) => {
  const [talent, setTalent] = useState([]);
  const [tab, setTab] = useState("All");
  const [loading, setLoading] = useState(true);
  const { t, tCat } = useLanguage();

  useEffect(() => {
    setLoading(true);
    api
      .get("/talent", { params: { division } })
      .then((r) => setTalent(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [division]);

  const filtered = useMemo(
    () => (tab === "All" ? talent : talent.filter((x) => (x.categories || []).includes(tab))),
    [talent, tab]
  );

  return (
    <main data-testid={pageTestid} className="pt-28 lg:pt-36">
      <div className="mx-auto max-w-[1800px] px-4 sm:px-8 lg:px-16">
        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-gold sm:text-xs">
          {eyebrow}
        </p>
        <h1 className="mt-4 font-display text-5xl font-light uppercase leading-[0.95] tracking-tight text-ivory sm:text-7xl lg:text-8xl">
          {titleLines.map((line, i) => (
            <MaskedLine key={line} delay={0.1 + i * 0.12}>
              {i === titleLines.length - 1 ? <span className="italic text-gold">{line}</span> : line}
            </MaskedLine>
          ))}
        </h1>
        <FadeUp delay={0.3}>
          <p className="mt-8 max-w-2xl font-display text-xl font-light italic leading-relaxed text-white/75 sm:text-2xl">
            {intro}
          </p>
        </FadeUp>

        <FadeUp delay={0.2} className="mt-12 flex flex-wrap items-center gap-x-1 gap-y-3 border-y border-white/10 py-5">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`filter-tab-${slug(c)}`}
              onClick={() => setTab(c)}
              className={`px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                tab === c ? "bg-ivory text-black" : "text-white/55 hover:text-gold"
              }`}
            >
              {tCat(c)}
            </button>
          ))}
          <span data-testid={`${pageTestid}-count`} className="ml-auto font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
            {filtered.length} {t("dir.represented")}
          </span>
        </FadeUp>

        {note && (
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">{note}</p>
        )}

        {loading ? (
          <div className="py-32 text-center font-mono text-xs uppercase tracking-[0.3em] text-white/40">
            {t("dir.loading")}
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid={`${pageTestid}-empty`} className="py-32 text-center">
            <p className="font-display text-2xl font-light italic text-white/60">
              {t("dir.emptyTitle")}
            </p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/35">
              {t("dir.emptyNote")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 pb-24 pt-12 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8 xl:grid-cols-4">
            {filtered.map((talentItem, i) => (
              <TalentCard key={talentItem.id} talent={talentItem} index={i} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
};
